import { useState, useEffect, useCallback, type ReactNode, type FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { ArrowRight, RefreshCw, Percent, DollarSign, Truck, X } from "lucide-react";

interface CouponData {
  readonly id?: string;
  readonly code: string;
  readonly discountType: string;
  readonly discountValue: number;
  readonly minOrderAmount: number | null;
  readonly maxUses: number | null;
  readonly maxUsesPerCustomer: number | null;
  readonly startsAt: string | null;
  readonly expiresAt: string | null;
  readonly isActive: boolean;
}

function generateCode(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function formatPreview(type: string, value: number, minOrder: number | null): string {
  let text = "קופון זה נותן ";
  if (type === "percentage") text += `הנחה של ${value}%`;
  else if (type === "fixed") text += `הנחה של ₪${(value / 100).toFixed(2)}`;
  else text += "משלוח חינם";
  if (minOrder) text += ` להזמנות מעל ₪${(minOrder / 100).toFixed(2)}`;
  return text;
}

const DISCOUNT_TYPES = [
  { value: "percentage", label: "אחוז %", icon: Percent },
  { value: "fixed",      label: "סכום קבוע ₪", icon: DollarSign },
  { value: "free_shipping", label: "משלוח חינם", icon: Truck },
] as const;

function FormField({ label, hint, children }: { readonly label: string; readonly hint?: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium leading-none">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function CouponFormPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = id !== undefined && id !== "new";

  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValueInput, setDiscountValueInput] = useState("");
  const [minOrderInput, setMinOrderInput] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [maxUsesPerCustomer, setMaxUsesPerCustomer] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) {
      setCode(generateCode());
      return;
    }
    void (async () => {
      const res = await fetch("/api/coupons", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: readonly CouponData[] };
      if (json.success) {
        const coupon = json.data.find((c) => c.id === id);
        if (coupon) {
          setCode(coupon.code);
          setDiscountType(coupon.discountType);
          setDiscountValueInput(
            coupon.discountType === "fixed"
              ? (coupon.discountValue / 100).toString()
              : coupon.discountValue.toString(),
          );
          setMinOrderInput(coupon.minOrderAmount ? (coupon.minOrderAmount / 100).toString() : "");
          setMaxUses(coupon.maxUses?.toString() ?? "");
          setMaxUsesPerCustomer(coupon.maxUsesPerCustomer?.toString() ?? "");
          setStartsAt(coupon.startsAt?.slice(0, 10) ?? "");
          setExpiresAt(coupon.expiresAt?.slice(0, 10) ?? "");
          setIsActive(coupon.isActive);
        }
      }
    })();
  }, [id, isEdit, accessToken]);

  const discountValueAgorot = discountType === "fixed"
    ? Math.round(Number(discountValueInput) * 100)
    : Number(discountValueInput);
  const minOrderAgorot = minOrderInput ? Math.round(Number(minOrderInput) * 100) : null;

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    const payload = {
      code: code.toUpperCase(),
      discountType,
      discountValue: discountValueAgorot,
      minOrderAmount: minOrderAgorot,
      maxUses: maxUses ? Number(maxUses) : null,
      maxUsesPerCustomer: maxUsesPerCustomer ? Number(maxUsesPerCustomer) : null,
      startsAt: startsAt ? `${startsAt}T00:00:00Z` : null,
      expiresAt: expiresAt ? `${expiresAt}T23:59:59Z` : null,
      isActive,
    };

    try {
      const url = isEdit ? `/api/coupons/${id}` : "/api/coupons";
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (json.success) {
        navigate("/coupons");
      } else {
        setError(json.error ?? "שגיאה בשמירה");
      }
    } finally {
      setSaving(false);
    }
  }, [code, discountType, discountValueAgorot, minOrderAgorot, maxUses, maxUsesPerCustomer, startsAt, expiresAt, isActive, isEdit, id, accessToken, navigate]);

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/coupons")} className="min-h-[38px]">
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEdit ? "עריכת קופון" : "קופון חדש"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isEdit ? "ערוך את פרטי הקופון" : "צור קופון הנחה חדש"}
          </p>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {/* Code */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">קוד קופון</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="font-mono flex-1 min-h-[44px] text-base tracking-widest"
                placeholder="SUMMER2026"
                required
                dir="ltr"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setCode(generateCode())}
                className="min-h-[44px] shrink-0"
                title="צור קוד אקראי"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Discount type + value */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">סוג הנחה</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Type selector */}
            <div className="grid grid-cols-3 gap-2">
              {DISCOUNT_TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setDiscountType(value); setDiscountValueInput(""); }}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-xl border text-center transition-all min-h-[72px]",
                    discountType === value
                      ? "border-primary bg-primary/8 shadow-sm"
                      : "hover:bg-muted/40 hover:border-muted-foreground/30"
                  )}
                >
                  <Icon className={cn("h-5 w-5", discountType === value ? "text-primary" : "text-muted-foreground")} />
                  <span className={cn("text-xs font-medium leading-tight", discountType === value ? "text-primary" : "")}>{label}</span>
                </button>
              ))}
            </div>

            {/* Value input */}
            {discountType !== "free_shipping" && (
              <FormField
                label={discountType === "percentage" ? "אחוז הנחה (1–100)" : "סכום הנחה (₪)"}
              >
                <div className="relative">
                  <Input
                    type="number"
                    step={discountType === "fixed" ? "0.01" : "1"}
                    min={discountType === "percentage" ? "1" : "0.01"}
                    max={discountType === "percentage" ? "100" : undefined}
                    value={discountValueInput}
                    onChange={(e) => setDiscountValueInput(e.target.value)}
                    required
                    className="pe-8 min-h-[44px]"
                  />
                  <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                    {discountType === "percentage" ? "%" : "₪"}
                  </span>
                </div>
              </FormField>
            )}

            {/* Preview */}
            {(discountValueInput || discountType === "free_shipping") && (
              <div className="flex items-start gap-2 bg-primary/8 border border-primary/20 rounded-lg p-3 text-sm">
                <span className="text-primary font-medium">{formatPreview(discountType, discountValueAgorot, minOrderAgorot)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Limits */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">הגבלות</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="הזמנה מינימלית (₪)">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={minOrderInput}
                onChange={(e) => setMinOrderInput(e.target.value)}
                placeholder="ללא הגבלה"
                className="min-h-[42px]"
              />
            </FormField>
            <FormField label="מקסימום שימושים">
              <Input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="ללא הגבלה"
                className="min-h-[42px]"
              />
            </FormField>
            <FormField label="מקסימום ללקוח">
              <Input
                type="number"
                min="1"
                value={maxUsesPerCustomer}
                onChange={(e) => setMaxUsesPerCustomer(e.target.value)}
                placeholder="ללא הגבלה"
                className="min-h-[42px]"
              />
            </FormField>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">תקופת תוקף</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="מתאריך">
              <Input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="min-h-[42px]" />
            </FormField>
            <FormField label="עד תאריך">
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="min-h-[42px]" />
            </FormField>
          </CardContent>
        </Card>

        {/* Active toggle */}
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">קופון פעיל</p>
              <p className="text-xs text-muted-foreground mt-0.5">לקוחות יוכלו להשתמש בקופון זה</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              className={cn(
                "relative h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                isActive ? "bg-primary" : "bg-muted"
              )}
              onClick={() => setIsActive(!isActive)}
            >
              <span
                className={cn(
                  "block h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                  isActive ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0.5 rtl:-translate-x-0.5"
                )}
              />
            </button>
          </CardContent>
        </Card>

        {error && (
          <div className="flex items-center gap-2 bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
            <X className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-1 border-t">
          <Button type="submit" disabled={saving} className="min-h-[44px]">
            {saving ? "שומר..." : isEdit ? "עדכון" : "צור קופון"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/coupons")} className="min-h-[44px]">
            ביטול
          </Button>
        </div>
      </form>
    </div>
  );
}
